import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { diagnosticsSnapshot } from '@/lib/diagnostics';

/** Плавающая кнопка «Идея / Проблема» (перенос из zakup.starec.ai, ТЗ п.23.3): текст, тип, снимок экрана, контекст страницы. */
export const FEEDBACK_KINDS = ['bug', 'net', 'mesh', 'idea', 'feat'] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];

async function captureScreen(exclude: HTMLElement | null): Promise<Blob | null> {
  const timeout = new Promise<null>((r) => setTimeout(() => r(null), 5000));
  const work = (async () => {
    try {
      const { default: html2canvas } = await import('html2canvas-pro');
      const root = document.getElementById('root');
      if (!root) return null;
      const canvas = await html2canvas(root, {
        scale: Math.min(devicePixelRatio || 1, 1.5),
        backgroundColor: getComputedStyle(document.body).backgroundColor || '#fff',
        logging: false,
        useCORS: true,
        ignoreElements: (el) => !!exclude && exclude.contains(el),
        width: innerWidth,
        height: innerHeight,
        windowWidth: innerWidth,
        windowHeight: innerHeight,
        x: scrollX,
        y: scrollY,
      });
      return await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.72));
    } catch {
      return null;
    }
  })();
  return Promise.race([work, timeout]);
}

export default function FeedbackWidget({ screen, listPath }: { screen: string; listPath: string }) {
  const { t, i18n } = useTranslation();
  const { me, version } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [message, setMessage] = useState('');
  const [withShot, setWithShot] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [sentId, setSentId] = useState<number | null>(null);
  const [error, setError] = useState('');
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    setSentId(null);
    setError('');
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!message.trim() || busy) return;
    setBusy(true);
    setError('');
    try {
      let shot: Blob | null = file;
      if (!shot && withShot) {
        setCapturing(true);
        shot = await captureScreen(box.current);
        setCapturing(false);
      }
      const form = new FormData();
      form.append('kind', kind);
      form.append('message', message.trim());
      form.append('page', location.pathname + location.search);
      form.append('screen', screen);
      form.append('lang', i18n.language === 'ru' ? 'ru' : 'en');
      form.append('context', JSON.stringify(diagnosticsSnapshot({ role: me?.role, side: me?.side, build: version })));
      if (shot) form.append('screenshot', shot, file?.name ?? 'screenshot.jpg');
      const res = await api.upload<{ id: number }>('/feedback', form);
      setSentId(res.id);
      setMessage('');
      setFile(null);
      window.dispatchEvent(new Event('feedback:changed'));
    } catch {
      setError(t('fb.failed'));
    } finally {
      setCapturing(false);
      setBusy(false);
    }
  };

  const input = 'h-9 w-full rounded-md border border-background-300 bg-background-50 px-2.5 text-[12px] text-foreground-900 outline-none focus:border-primary-400';

  return (
    <div ref={box} className="fixed bottom-[72px] right-3 z-40 flex flex-col items-end gap-2 sm:bottom-5 sm:right-5 lg:bottom-5">
      {open && (
        <div className="w-[calc(100vw-24px)] max-w-[360px] rounded-lg border border-background-200 bg-background-50 p-4 shadow-[0_18px_45px_-20px_rgba(15,23,42,0.35)]">
          {sentId !== null ? (
            <div>
              <p className="text-[13px] font-semibold text-foreground-950">{t('fb.thanks', { id: sentId })}</p>
              <p className="mt-1 text-[11.5px] leading-4 text-foreground-500">{t('fb.thanksHint')}</p>
              <div className="mt-3 flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    close();
                    navigate(`${listPath}#${sentId}`);
                  }}
                  className="h-8 cursor-pointer rounded-md bg-primary-600 px-3 text-[12px] font-semibold text-background-50 hover:bg-primary-700"
                >
                  {t('fb.myItems')}
                </button>
                <button type="button" onClick={close} className="h-8 cursor-pointer rounded-md border border-background-300 px-3 text-[12px] font-medium text-foreground-700 hover:bg-background-100">
                  {t('fb.close')}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="flex items-center justify-between">
                <p className="text-[13px] font-semibold text-foreground-950">{t('fb.title')}</p>
                <button type="button" onClick={close} aria-label={t('fb.close')} className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-foreground-400 hover:bg-background-100 hover:text-foreground-800">
                  <i className="ri-close-line text-base" />
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {FEEDBACK_KINDS.map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setKind(k)}
                    className={`cursor-pointer whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                      kind === k ? 'border-primary-200 bg-primary-50 text-primary-800' : 'border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100'
                    }`}
                  >
                    {t(`fb.kind.${k}`)}
                  </button>
                ))}
              </div>
              <textarea
                autoFocus
                rows={5}
                maxLength={4000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={kind === 'bug' ? t('fb.placeholderBug') : t('fb.placeholderIdea')}
                className="mt-2 w-full rounded-md border border-background-300 bg-background-50 px-2.5 py-2 text-[12px] leading-5 text-foreground-900 outline-none focus:border-primary-400"
              />
              <label className="mt-2 flex cursor-pointer items-center gap-2 text-[11.5px] text-foreground-700">
                <input type="checkbox" checked={withShot && !file} disabled={!!file} onChange={(e) => setWithShot(e.target.checked)} className="h-3.5 w-3.5 accent-primary-600" />
                {t('fb.attachShot')}
              </label>
              <label className="mt-1.5 block text-[11px] text-foreground-500">
                {t('fb.orFile')}
                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="mt-1 block w-full text-[11px]" />
              </label>
              <p className="mt-2 text-[10.5px] text-foreground-400">{t('fb.screen', { screen })}</p>
              {error && <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11.5px] text-rose-700">{error}</p>}
              <button
                type="submit"
                disabled={!message.trim() || busy}
                className={`mt-3 ${input} flex cursor-pointer items-center justify-center gap-1.5 border-0 bg-primary-600 font-semibold text-background-50 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {capturing ? t('fb.capturing') : busy ? t('fb.sending') : t('fb.send')}
              </button>
            </form>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        className="flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-200 bg-background-50 px-3.5 text-[12px] font-semibold text-primary-800 shadow-[0_8px_24px_-12px_rgba(15,23,42,0.4)] transition-colors hover:bg-primary-50"
      >
        <i className="ri-lightbulb-flash-line text-[14px]" />
        <span className="hidden sm:inline">{t('fb.button')}</span>
      </button>
    </div>
  );
}
